import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDecidePurchaseInvoiceProof, usePurchaseInvoice } from '../api/hooks';
import { ApiError } from '../api/client';
import { useSession } from '../auth/session';
import { Alert, Button, Field, Modal, PageHeader, Spinner } from '../ui';
import { PurchaseInvoiceBody, PurchaseInvoiceStateChip, type PurchaseInvoiceLabels } from './purchase-invoice/PurchaseInvoiceBody';
import { DownloadPdfButton } from './purchase-invoice/DownloadPdfButton';

/**
 * A-45 — the purchase invoice behind a row on A-40 (CR-56 P1; FN-04, FN-17), and where Admin decides
 * the retailer's claims to have paid it (CR-56 P4).
 *
 * Reading is read-only. The one thing it can do is verify or reject a PENDING claim, behind
 * `payment.verify` — the permission that already covers every payment proof; no new code (D90/D91
 * are splitting `order.approve`, and a new one here would collide). Verifying calls the API's one
 * confirm path: that is what moves the payable, the invoice and the budget, together, and only
 * then. The staff console, so its words are literals; R-27 is the retailer's twin.
 */
const LABELS: PurchaseInvoiceLabels = {
  piecesTitle: 'Pieces on this invoice',
  piecesEmpty: 'No pieces are recorded against this invoice.',
  colProduct: 'Product',
  colVariant: 'Variant',
  colQuantity: 'Qty',
  subtotal: 'Pieces subtotal',
  adjustments: 'Adjustments (tax and other)',
  total: 'Invoice total',
  paymentsTitle: 'Payments applied',
  paymentsEmpty: 'Nothing has been paid against this invoice yet.',
  colReceived: 'Received',
  colBankReference: 'Bank reference',
  colAmount: 'Amount',
  paid: 'Paid',
  outstanding: 'Outstanding',
  submissionsTitle: 'Payments claimed by the retailer',
  submissionsNote: 'These are claims, not payments. Nothing below is in Paid or Outstanding until you verify it.',
  colSubmitted: 'Submitted',
  colStatus: 'Status',
  colProof: 'Proof',
  viewProof: 'View proof',
  noProof: 'Not attached',
  submissionStatus: { PENDING: 'Awaiting verification', REJECTED: 'Rejected' },
  state: { ISSUED: 'Issued', PART_PAID: 'Part paid', PAID: 'Paid', CANCELLED: 'Cancelled' },
};

/** Verify or reject one PENDING claim. */
function ProofActions({ proofId, onDone }: { proofId: string; onDone: (message: string | null) => void }) {
  const decide = useDecidePurchaseInvoiceProof();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const failure = (e: unknown) => (e instanceof ApiError ? e.message : 'That could not be done.');

  return (
    <>
      <div style={{ display: 'flex', gap: 6 }}>
        <Button
          size="sm" disabled={decide.isPending}
          onClick={() => {
            onDone(null);
            decide.mutate({ proofId, decision: 'verify' }, { onError: (e) => onDone(failure(e)) });
          }}
        >
          Verify
        </Button>
        <Button size="sm" tone="ghost" disabled={decide.isPending} onClick={() => setRejecting(true)}>Reject</Button>
      </div>
      {rejecting ? (
        <Modal
          title="Reject this payment"
          onClose={() => setRejecting(false)}
          footer={(
            <>
              <Button tone="ghost" onClick={() => setRejecting(false)}>Cancel</Button>
              <Button
                disabled={reason.trim().length < 3 || decide.isPending}
                onClick={() => {
                  onDone(null);
                  decide.mutate({ proofId, decision: 'reject', reason: reason.trim() }, {
                    onSuccess: () => setRejecting(false),
                    onError: (e) => { setRejecting(false); onDone(failure(e)); },
                  });
                }}
              >
                Reject payment
              </Button>
            </>
          )}
        >
          <Field label="Reason" hint="The retailer sees this, so they know what to fix before sending it again.">
            <textarea rows={3} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </Modal>
      ) : null}
    </>
  );
}

export function PurchaseInvoiceDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const { data: inv, isLoading, error } = usePurchaseInvoice(id);
  const { can } = useSession();
  const [problem, setProblem] = useState<string | null>(null);

  const back = <div className="mk-backlink"><Link to="/payables">&larr; Sales &amp; payables</Link></div>;

  if (isLoading) return <div role="status"><Spinner /></div>;
  if (error) {
    return (
      <>
        {back}
        <Alert tone="stop">
          {error instanceof ApiError && error.status === 404
            ? 'That invoice does not exist on this portal.'
            : 'The invoice could not be loaded.'}
        </Alert>
      </>
    );
  }
  if (!inv) return null;

  return (
    <>
      {back}
      <PageHeader
        title={inv.reference}
        sub={(
          <>
            <Link to={`/retailers/${inv.retailerId}`}>{inv.retailerName}</Link>
            {` · Issued ${inv.issuedOn} · Due ${inv.dueOn}${inv.period ? ` · Period ${inv.period}` : ''}`}
          </>
        )}
        actions={(
          <>
            <PurchaseInvoiceStateChip state={inv.state} labels={LABELS} />
            <DownloadPdfButton
              path={`/payables/invoices/${inv.id}/download`}
              fallbackName={`${inv.reference.replace(/[^A-Za-z0-9_-]/g, '_')}.pdf`}
              label="Download PDF"
              failed="The PDF could not be downloaded."
            />
          </>
        )}
      />
      {problem ? <Alert tone="stop">{problem}</Alert> : null}
      <PurchaseInvoiceBody
        inv={inv}
        labels={LABELS}
        fileHref={(proofId) => `/api/payments/${proofId}/file`}
        {...(can('payment.verify')
          ? { actions: (s) => (s.status === 'PENDING' ? <ProofActions proofId={s.proofId} onDone={setProblem} /> : null) }
          : {})}
      />
    </>
  );
}
